import {
	HttpContextToken,
	HttpErrorResponse,
	HttpEvent,
	HttpInterceptorFn,
	HttpParams,
	HttpRequest,
	HttpResponse,
	HttpUrlEncodingCodec,
} from "@angular/common/http";
import { ApplicationRef, inject, runInInjectionContext } from "@angular/core";
import { catchError, map, switchMap } from "rxjs/operators";
import { from, throwError } from "rxjs";

import { hasProperty } from "core/functions";
import {
	MainService,
	SECONDARY_MODE_PARAM,
	SECONDARY_PROCESS_VERSION_PARAM,
	SECONDARY_WORKSPACE_ID_PARAM,
} from "core/main.service";
import { LoginOrchestrator } from "core/auth/login-orchestrator.service";

export const REQUEST_START_TIME = new HttpContextToken<number>(() => Date.now());

export interface StagesHttpResponseBody {
	_appContext: stages.context.ApplicationContext;
	_entity: unknown;
	_workspaceView: stages.workspace.application.WorkspaceView;
}

export const stagesHttpInterceptor: HttpInterceptorFn = (req, next) => {
	const mainService = inject(MainService);
	const appRef = inject(ApplicationRef);

	const normalizedReq = req.clone({
		headers: req.headers.get("Cache-Control")
			? req.headers
			: req.headers.append("Cache-Control", "no-cache").append("Pragma", "no-cache"),
		params: appendSecondaryViewParamsIfSet(mainService, replaceAngularHttpParameterCodec(req.params)),
		context: req.context.set(REQUEST_START_TIME, Date.now()),
	});

	return next(normalizedReq).pipe(
		catchError((err: unknown) =>
			from(handleAuthNavigation({ err, req: normalizedReq, appRef })).pipe(
				switchMap((maybeEvent) => (maybeEvent ? [maybeEvent] : throwError(() => err))),
			),
		),
		map((event) => setContextAndUnwrap(event, req, mainService)),
	);
};

function appendSecondaryViewParamsIfSet(mainService: MainService, params: HttpParams): HttpParams {
	const secondaryWorkspaceId = mainService.secondaryWorkspaceId;
	const secondaryProcessVersion = mainService.secondaryProcessVersion;

	const withSecondary =
		secondaryWorkspaceId && secondaryProcessVersion
			? params
					.append(SECONDARY_WORKSPACE_ID_PARAM, secondaryWorkspaceId)
					.append(SECONDARY_PROCESS_VERSION_PARAM, secondaryProcessVersion)
			: params;

	const secondaryMode = mainService.secondaryMode;
	return secondaryMode ? withSecondary.append(SECONDARY_MODE_PARAM, secondaryMode) : withSecondary;
}

let lastContextUpdateStartedByRequestStartTime: number | undefined = undefined;

function setContextAndUnwrap(
	event: HttpEvent<unknown>,
	request: HttpRequest<unknown>,
	mainService: MainService,
): HttpEvent<unknown> {
	if (
		event instanceof HttpResponse &&
		event.body &&
		hasProperty(event.body, "_workspaceView") &&
		hasProperty(event.body, "_appContext")
	) {
		const body = event.body as StagesHttpResponseBody;
		if (
			lastContextUpdateStartedByRequestStartTime === undefined ||
			request.context.get(REQUEST_START_TIME) > lastContextUpdateStartedByRequestStartTime
		) {
			mainService.refreshCurrentWorkspaceAndAppContext(body._workspaceView, body._appContext);
			lastContextUpdateStartedByRequestStartTime = request.context.get(REQUEST_START_TIME);
		}
		return event.clone({ body: body._entity });
	}
	return event;
}

/**
 * Auth handling rule-of-thumb:
 * - Interceptor does NOT initiate login (no popup, no redirects to IdP).
 * - It only routes user to /login, and guards/orchestrator take it from there.
 */
async function handleAuthNavigation(opts: {
	err: unknown;
	req: HttpRequest<unknown>;
	appRef: ApplicationRef;
}): Promise<HttpEvent<unknown> | null> {
	const { err, req, appRef } = opts;

	if (!(err instanceof HttpErrorResponse)) {
		return null;
	}

	// Don’t interfere with the endpoints that define auth state / login itself.
	if (isAuthRelatedRequest(req)) {
		return null;
	}

	if (err.status === 401) {
		const login = runInInjectionContext(appRef.injector, () => inject(LoginOrchestrator));
		// we need to handle 401 here as the window location will be changed until we reach the error-handler
		// so we can save redirectTo only here
		await login.tryPassiveSamlOnceAndNavigateAfterHttpError401();
		return null;
	}

	// For other status codes, do nothing special.
	return null;
}

function isAuthRelatedRequest(req: HttpRequest<unknown>): boolean {
	// Keep these conservative; adjust to your actual endpoints.
	const url = req.url;

	// calling the auth endpoints should not trigger an auth redirect loop
	return url.includes("auth/");
}

/**
 * Minimal invasive fix for ST-31398:
 * https://stackoverflow.com/questions/45428842/angular-url-plus-sign-converting-to-space/52458069#52458069
 * https://github.com/angular/angular/issues/18261
 * https://github.com/angular/angular/pull/32598
 */
function replaceAngularHttpParameterCodec(httpParams: HttpParams): HttpParams {
	return new HttpParams({ encoder: new StagesHttpParameterCodec(), fromString: httpParams.toString() });
}

class StagesHttpParameterCodec extends HttpUrlEncodingCodec {
	override encodeKey(k: string): string {
		return standardEncoding(k);
	}
	override encodeValue(v: string): string {
		return standardEncoding(v);
	}
}

/**
 * Largely copied from https://github.com/angular/angular/blob/master/packages/common/http/src/params.ts
 * but do not replace "%2B" with "+", because "+" will be interpreted as " " by Stages server.
 * Perhaps other replacements should also not be done. E.g. replacing "%3D" with "=" could be a problem for keys.
 */
function standardEncoding(v: string): string {
	return (
		encodeURIComponent(v)
			.replace(/%40/gi, "@")
			.replace(/%3A/gi, ":")
			.replace(/%24/gi, "$")
			.replace(/%2C/gi, ",")
			.replace(/%3B/gi, ";")
			//.replace(/%2B/gi, '+')
			.replace(/%3D/gi, "=")
			.replace(/%3F/gi, "?")
			.replace(/%2F/gi, "/")
	);
}
